import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Badge,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  Settings,
  ExitToApp,
  Person,
  DarkMode,
  LightMode,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import EventIcon from '@mui/icons-material/Event';
import AssignmentIcon from '@mui/icons-material/Assignment';

const Navbar = ({ open, drawerWidth, handleDrawerToggle, handleMobileDrawerToggle }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useAppTheme();
  const { showNotification } = useNotification();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationsMenuOpen = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsMenuClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    showNotification('Has cerrado sesión correctamente', 'success');
    handleMenuClose();
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { sm: open ? `calc(100% - ${drawerWidth}px)` : '100%' },
        ml: { sm: open ? `${drawerWidth}px` : 0 },
        boxShadow: '0 2px 10px 0 rgba(0,0,0,0.1)',
        backgroundColor: 'background.paper',
        color: 'text.primary',
        transition: muiTheme.transitions.create(['width', 'margin'], {
          easing: muiTheme.transitions.easing.sharp,
          duration: muiTheme.transitions.duration.leavingScreen,
        }),
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="abrir cajón"
          edge="start"
          onClick={isMobile ? handleMobileDrawerToggle : handleDrawerToggle}
          sx={{ marginRight: 2 }}
        >
          <MenuIcon />
        </IconButton>
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{ display: { xs: 'none', sm: 'block' } }}
        >
          {process.env.REACT_APP_APP_NAME || 'NeuroPsico Cusco'}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex' }}>
          <Tooltip title="Cambiar tema">
            <IconButton 
              color="inherit" 
              onClick={toggleTheme}
              size="large"
            >
              {theme === 'dark' ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Notificaciones">
            <IconButton
              color="inherit"
              onClick={handleNotificationsMenuOpen}
              size="large"
            >
              <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Perfil">
            <IconButton
              edge="end"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
              size="large"
            >
              {user?.foto ? (
                <Avatar 
                  alt={`${user.nombre} ${user.apellidos}`}
                  src={user.foto}
                  sx={{ width: 32, height: 32 }}
                />
              ) : (
                <AccountCircle />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>

      {/* Menú de perfil */}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle1" noWrap>
            {user ? `${user.nombre} ${user.apellidos}` : 'Usuario'}
          </Typography>
          <Typography variant="body2" color="textSecondary" noWrap>
            {user?.email || 'usuario@example.com'}
          </Typography>
        </Box>
        <Divider />
        <MenuItem component={RouterLink} to="/perfil" onClick={handleMenuClose}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Mi Perfil" />
        </MenuItem>
        <MenuItem component={RouterLink} to="/configuracion" onClick={handleMenuClose}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Configuración" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <ExitToApp fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Cerrar Sesión" />
        </MenuItem>
      </Menu>

      {/* Menú de notificaciones */}
      <Menu
        anchorEl={notificationsAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(notificationsAnchorEl)}
        onClose={handleNotificationsMenuClose}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle1">Notificaciones</Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleNotificationsMenuClose}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="Nuevo paciente registrado" 
            secondary="Hace 5 minutos" 
          />
        </MenuItem>
        <MenuItem onClick={handleNotificationsMenuClose}>
          <ListItemIcon>
            <EventIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="Cita confirmada" 
            secondary="Hace 30 minutos" 
          />
        </MenuItem>
        <MenuItem onClick={handleNotificationsMenuClose}>
          <ListItemIcon>
            <AssignmentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="Nueva evaluación completada" 
            secondary="Hace 2 horas" 
          />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleNotificationsMenuClose} sx={{ justifyContent: 'center' }}>
          <Typography variant="body2" color="primary">Ver todas</Typography>
        </MenuItem>
      </Menu>
    </AppBar>
  );
};

export default Navbar;